import { Box, Button, PageHeader, Select } from '@tocktix/pantry';
import { ChangeEvent, FunctionComponent, useCallback, useEffect, useState } from 'react';
import { userClient } from 'tools/bff/client';
import BulkUserDeactivateAction from 'tools/users/BulkUserDeactivateAction';
import BulkUserInput from 'tools/users/BulkUserInput';
import BulkUserPreviewTable, { DeactivatableUser } from 'tools/users/BulkUserPreviewTable';
import BulkUserProgress from 'tools/users/BulkUserProgress';

type Step = 'input' | 'preview' | 'running';

// Bulk actions the operator can run against the resolved users. Deactivation is the only action
// today; each action owns a component with its own fields and submit logic, so the form shows
// nothing action-specific until one is picked. Add future actions here and render their component
// in the switch below.
const DEACTIVATE_ACTION = 'deactivate';

const ACTION_OPTIONS = [{ value: DEACTIVATE_ACTION, label: 'Deactivate users' }];

const BulkUserActionsRoute: FunctionComponent = () => {
  const [step, setStep] = useState<Step>('input');
  const [identifiers, setIdentifiers] = useState<string[]>([]);
  const [deactivatable, setDeactivatable] = useState<DeactivatableUser[]>([]);
  const [action, setAction] = useState('');
  const [attachedFromStart, setAttachedFromStart] = useState(false);

  // Resume after refresh: one status probe on mount. The workflow id is deterministic per operator
  // (bulk-user-deactivate-<adminId>), so a still-running job can be re-displayed with nothing to
  // persist. NOT_FOUND (no active job) and transient errors both leave the operator on input.
  useEffect(() => {
    let mounted = true;
    userClient
      .getBulkUserActionStatus({})
      .then((status) => {
        if (mounted && !status.done) {
          setStep('running');
        }
      })
      .catch(() => {
        // Intentionally idle: no active job or a transient probe error keeps the input step.
      });
    return () => {
      mounted = false;
    };
  }, []);

  const handleDeactivatableChange = useCallback((users: DeactivatableUser[]) => {
    setDeactivatable(users);
  }, []);

  const handleStarted = useCallback((attachedToExistingRun: boolean) => {
    setAttachedFromStart(attachedToExistingRun);
    setStep('running');
  }, []);

  const reset = () => {
    setStep('input');
    setIdentifiers([]);
    setDeactivatable([]);
    setAction('');
    setAttachedFromStart(false);
  };

  const handleContinue = (ids: string[]) => {
    setIdentifiers(ids);
    setDeactivatable([]);
    setAction('');
    setStep('preview');
  };

  return (
    <Box padding={[0, 500, 500]}>
      <PageHeader title="Bulk user actions" />
      <Box padding={[300, 0]} display="flex" flexDirection="column" gap={400}>
        {step === 'input' && (
          <BulkUserInput initialValue={identifiers.join('\n')} onContinue={handleContinue} />
        )}

        {step === 'preview' && (
          <>
            <BulkUserPreviewTable
              identifiers={identifiers}
              onDeactivatableChange={handleDeactivatableChange}
            />
            <Box display="flex" flexDirection="column" gap={300} style={{ maxWidth: '480px' }}>
              <Select
                id="bulk-user-action"
                label="Action"
                placeholder="Select an action"
                value={action}
                onChange={(event: ChangeEvent<HTMLSelectElement>) => setAction(event.target.value)}
                required
                fullWidth
                data-testid="bulk-user-action-select"
              >
                {ACTION_OPTIONS.map(({ value, label }) => (
                  <Select.Option key={value} value={value}>
                    {label}
                  </Select.Option>
                ))}
              </Select>
            </Box>

            {action === DEACTIVATE_ACTION ? (
              <BulkUserDeactivateAction
                deactivatable={deactivatable}
                onStarted={handleStarted}
                onBack={() => setStep('input')}
              />
            ) : (
              <Box display="flex" gap={200}>
                <Button
                  variant="outlined"
                  label="Back"
                  onClick={() => setStep('input')}
                  data-testid="bulk-user-back-button"
                />
              </Box>
            )}
          </>
        )}

        {step === 'running' && (
          <BulkUserProgress attachedFromStart={attachedFromStart} onStartAnother={reset} />
        )}
      </Box>
    </Box>
  );
};

export default BulkUserActionsRoute;
